<template>
  <v-dialog v-model="dialog" max-width="700px" persistent>
    <v-card>
      <v-card-title class="primary--text">
        {{ isEdit ? 'Editar Torneo' : 'Crear Nuevo Torneo' }}
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <v-form ref="form" @submit.prevent="handleSubmit">
          <!-- Nombre -->
          <v-text-field
            v-model="form.name"
            label="Nombre del Torneo"
            outlined
            dense
            required
            :rules="[rules.required]"
            class="mb-4"
          ></v-text-field>

          <!-- Descripción -->
          <v-textarea
            v-model="form.description"
            label="Descripción"
            outlined
            dense
            rows="3"
            class="mb-4"
          ></v-textarea>

          <!-- Imagen del Torneo -->
          <div class="mb-4">
            <v-file-input
              v-model="imageFile"
              label="Imagen del Torneo"
              accept="image/*"
              outlined
              dense
              prepend-icon="mdi-image"
              @change="onImageSelected"
              class="mb-2"
            ></v-file-input>
            <div v-if="form.image" class="mt-2">
              <img
                :src="form.image"
                :alt="form.name"
                style="max-width: 200px; max-height: 150px; border-radius: 4px;"
              >
            </div>
          </div>

          <!-- QR y Enlace de Registro -->
          <div class="mb-4">
            <label class="font-weight-bold text-caption d-block mb-3">Código QR y Enlace de Registro</label>
            <v-row>
              <v-col cols="12" sm="6" class="d-flex justify-center">
                <div v-if="qrCodeUrl">
                  <img
                    :src="qrCodeUrl"
                    :alt="'QR Code'"
                    style="max-width: 150px; border: 1px solid #ddd; padding: 5px; border-radius: 4px;"
                  >
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="registrationLink"
                  label="Enlace de Registro"
                  outlined
                  dense
                  readonly
                  append-icon="mdi-content-copy"
                  @click:append="copyToClipboard"
                  class="mb-2"
                ></v-text-field>
                <div class="text-caption grey--text">
                  Código único: {{ form.registration_code }}
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Sección: Configuración del Torneo -->
          <div class="mb-4">
            <h3 class="subtitle-2 font-weight-bold mb-3 primary--text">
              <v-icon small class="mr-2">mdi-cog</v-icon>
              Configuración del Torneo
            </h3>

            <!-- Fechas -->
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.start_date"
                  label="Fecha de Inicio"
                  type="date"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.end_date"
                  label="Fecha de Fin"
                  type="date"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Categoría y Sistema de Juego -->
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.category"
                  :items="categories"
                  item-text="name"
                  item-value="id"
                  label="Categoría"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                >
                  <template v-slot:item="{ item }">
                    <div>
                      <strong>{{ item.name }}</strong>
                      <div v-if="item.handicapMin !== null" class="text-caption grey--text">
                        Índice: {{ item.handicapMin }} a {{ item.handicapMax }}
                      </div>
                      <div v-else class="text-caption grey--text">
                        Sin restricción de handicap
                      </div>
                    </div>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ item.name }}</span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.game_system"
                  :items="gameSystems"
                  item-text="name"
                  item-value="id"
                  label="Sistema de Juego"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
            </v-row>
          </div>

          <!-- Sección: Configuración de Handicap -->
          <div class="mb-4">
            <h3 class="subtitle-2 font-weight-bold mb-3 primary--text">
              <v-icon small class="mr-2">mdi-account-multiple</v-icon>
              Configuración de Handicap
            </h3>

            <v-checkbox
              v-model="form.handicap_free"
              label="Handicap Libre (sin restricción)"
              outlined
              dense
              class="mb-3"
            ></v-checkbox>

            <div v-if="!form.handicap_free" class="pa-3 bg-light rounded">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.handicap_min"
                    label="Handicap Mínimo"
                    type="number"
                    outlined
                    dense
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.handicap_max"
                    label="Handicap Máximo"
                    type="number"
                    outlined
                    dense
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="text-caption grey--text">
                Estos valores se establecen automáticamente según la categoría seleccionada.
              </div>
            </div>
            <div v-else class="pa-3 bg-light rounded">
              <v-alert type="info" dense small>
                Los jugadores de cualquier handicap podrán participar en este torneo.
              </v-alert>
            </div>
          </div>

          <!-- Sección: Canchas y Salidas -->
          <div class="mb-4">
            <h3 class="subtitle-2 font-weight-bold mb-3 primary--text">
              <v-icon small class="mr-2">mdi-golf</v-icon>
              Salidas
            </h3>

            <v-select
              v-model="form.courses"
              :items="courts"
              item-text="name"
              item-value="id"
              label="Seleccionar Salidas"
              outlined
              dense
              required
              multiple
              chips
              small-chips
              deletable-chips
              :rules="[rules.required, rules.minOneCourse]"
              class="mb-4"
              :loading="loadingCourts"
            >
              <template v-slot:item="{ item }">
                <div>
                  <strong>{{ item.name }}</strong>
                  <div class="text-caption grey--text">
                    {{ item.opening_time }} - {{ item.closing_time }} (Intervalo: {{ item.time_interval }} min)
                  </div>
                </div>
              </template>
            </v-select>
          </div>

          <!-- Sección: Capacidad y Estado -->
          <div class="mb-4">
            <h3 class="subtitle-2 font-weight-bold mb-3 primary--text">
              <v-icon small class="mr-2">mdi-settings</v-icon>
              Capacidad y Estado
            </h3>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="form.max_players"
                  label="Máx. Participantes"
                  type="number"
                  outlined
                  dense
                  required
                  :rules="[rules.required, rules.minValue]"
                  min="1"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.status"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="Estado"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
            </v-row>
          </div>

          <!-- Sección: Horarios de Salida -->
          <div class="mb-4">
            <h3 class="subtitle-2 font-weight-bold mb-3 primary--text">
              <v-icon small class="mr-2">mdi-clock</v-icon>
              Horarios de Salida
            </h3>

            <div v-if="form.courses.length > 0" class="pa-3 bg-light rounded mb-3">
              <div class="text-caption font-weight-bold">Salidas seleccionadas:</div>
              <div class="text-caption grey--text">{{ selectedCoursesNames }}</div>
            </div>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.start_time"
                  label="Hora de Inicio"
                  type="time"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.end_time"
                  label="Hora de Fin"
                  type="time"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="mt-3">
              <v-col cols="12" sm="6">
                <v-checkbox
                  v-model="form.starting_shotgun"
                  label="Escopetazo Inicial"
                ></v-checkbox>
              </v-col>
              <v-col v-if="form.starting_shotgun" cols="12" sm="6">
                <v-text-field
                  v-model="form.shotgun_time"
                  label="Hora del Escopetazo"
                  type="time"
                  outlined
                  dense
                  required
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-alert v-if="form.courses.length === 0" type="warning" dense small>
              Selecciona al menos una salida para establecer los horarios
            </v-alert>
          </div>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Cancelar</v-btn>
        <v-btn color="primary" :loading="loading" @click="handleSubmit">
          {{ isEdit ? 'Actualizar' : 'Crear' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { GOLF_CATEGORIES, GAME_SYSTEMS } from '../constants/tournamentConfig'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'CreateTournamentDialog',
  props: {
    tournament: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      dialog: true,
      loading: false,
      loadingCourts: false,
      courts: [],
      imageFile: null,
      qrCodeUrl: null,
      registrationLink: '',
      form: {
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        courses: [],
        category: 'championship',
        game_system: 'stroke_play',
        max_players: 20,
        handicap_min: null,
        handicap_max: null,
        handicap_free: false,
        start_time: '',
        end_time: '',
        starting_shotgun: false,
        shotgun_time: '',
        status: 1,
        image: null,
        registration_code: ''
      },
      categories: GOLF_CATEGORIES,
      gameSystems: GAME_SYSTEMS,
      statusOptions: [
        { text: 'Planificado', value: 1 },
        { text: 'Abierto inscripciones', value: 2 },
        { text: 'Inscripciones cerradas', value: 3 },
        { text: 'En curso', value: 4 },
        { text: 'Finalizado', value: 5 },
        { text: 'Cancelado', value: 6 }
      ],
      rules: {
        required: (v) => !!v || 'Campo requerido',
        minValue: (v) => v > 0 || 'Debe ser mayor a 0',
        minOneCourse: (v) => (Array.isArray(v) && v.length > 0) || 'Al menos una salida debe ser seleccionada'
      }
    }
  },
  computed: {
    isEdit () {
      return !!this.tournament
    },
    selectedCoursesNames () {
      return this.form.courses
        .map(courseId => {
          const court = this.courts.find(c => c.id === courseId)
          return court ? court.name : courseId
        })
        .join(', ')
    }
  },
  watch: {
    'form.registration_code' () {
      this.generateQRCode()
    },
    'form.category' () {
      this.updateHandicapRange()
    },
    'form.handicap_free' () {
      if (this.form.handicap_free) {
        this.form.handicap_min = null
        this.form.handicap_max = null
      } else {
        this.updateHandicapRange()
      }
    }
  },
  mounted () {
    this.loadCourts()
    if (this.tournament) {
      this.form = {
        ...this.tournament,
        courses: this.tournament.courses || [],
        start_time: this.tournament.start_time || '',
        end_time: this.tournament.end_time || '',
        starting_shotgun: this.tournament.starting_shotgun || false,
        shotgun_time: this.tournament.shotgun_time || '',
        image: this.tournament.image || null,
        registration_code: this.tournament.registration_code || this.generateRegistrationCode(),
        category: this.tournament.category || 'championship',
        game_system: this.tournament.game_system || 'stroke_play',
        handicap_free: this.tournament.handicap_free || false,
        handicap_min: this.tournament.handicap_min,
        handicap_max: this.tournament.handicap_max
      }
    } else {
      this.form.registration_code = this.generateRegistrationCode()
      this.updateHandicapRange()
    }
    this.generateQRCode()
  },
  methods: {
    async loadCourts () {
      this.loadingCourts = true
      try {
        const idHousingUnit = store.getters.getProfile?.idHousingUnit || 'a58d8679-e2ae-4055-a243-c63d438f19eb'
        const config = {
          method: 'get',
          url: `${API_BASE_URL}/golf-courses/?housing_unit=${idHousingUnit}`,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${store.getters.getToken}`
          }
        }
        const response = await axios(config)
        this.courts = response.data || []
      } catch (error) {
        console.error('Error loading courts:', error)
        this.$emit('error', 'No se pudieron cargar las salidas disponibles')
      } finally {
        this.loadingCourts = false
      }
    },
    onImageSelected (file) {
      if (!file) {
        this.form.image = null
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.form.image = e.target.result
      }
      reader.readAsDataURL(file)
    },
    generateRegistrationCode () {
      return 'TOUR-' + Date.now().toString(36).toUpperCase() + '-' + Math.random().toString(36).substr(2, 9).toUpperCase()
    },
    generateQRCode () {
      const domain = window.location.origin
      this.registrationLink = `${domain}/golf/tournament/register/${this.form.registration_code}`
      const encodedUrl = encodeURIComponent(this.registrationLink)
      this.qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedUrl}`
    },
    copyToClipboard () {
      navigator.clipboard.writeText(this.registrationLink).then(() => {
        this.$emit('success', 'Enlace copiado al portapapeles')
      })
    },
    updateHandicapRange () {
      const category = this.categories.find(cat => cat.id === this.form.category)
      if (category && !this.form.handicap_free) {
        this.form.handicap_min = category.handicapMin
        this.form.handicap_max = category.handicapMax
      }
    },
    async handleSubmit () {
      const formRef = this.$refs.form
      if (!formRef.validate()) return

      if (!this.form.courses || this.form.courses.length === 0) {
        this.$emit('error', 'Al menos una salida debe ser seleccionada')
        return
      }

      if (this.form.starting_shotgun && !this.form.shotgun_time) {
        this.$emit('error', 'La hora del escopetazo es requerida si seleccionas escopetazo inicial')
        return
      }

      this.loading = true
      try {
        const payload = {
          ...this.form,
          courses: this.form.courses,
          shotgun_time: this.form.starting_shotgun ? this.form.shotgun_time : null
        }

        if (this.isEdit) {
          this.$emit('update', payload)
        } else {
          this.$emit('create', payload)
        }
      } catch (err) {
        // Error manejado en el componente padre
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .v-dialog__container {
  align-items: flex-start;
  padding-top: 20px;
}

.bg-light {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
}

.subtitle-2 {
  font-size: 0.875rem;
}
</style>
